/**
 * MLCOReviewForm — drawer-embedded MLCO decision form (Brief 14c-ii).
 *
 * 4 outcomes (approve / approve_with_conditions / reject / escalate_to_colp).
 * Conditions textarea is visible+required only when outcome=approve_with_conditions.
 * 60s debounce via useRef-backed timestamp prevents double-submit.
 *
 * Role gate: form is only rendered if the caller is MLCO or COLP.
 * The drawer's existing read-only flag content + history remain visible
 * for non-MLCO/COLP users.
 */
import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "../../config/api";
import AMLProfileSignalsPanel from "./AMLProfileSignalsPanel";

import { errorMessage } from "../../utils/errorMessage";
const OUTCOMES = [
  {
    value: "approve",
    label: "Approve",
    description: "Risk is acceptable; matter may proceed.",
  },
  {
    value: "approve_with_conditions",
    label: "Approve with conditions",
    description: "Acceptable if specific safeguards are followed.",
  },
  {
    value: "reject",
    label: "Reject",
    description: "Risk is unacceptable; matter must not proceed.",
  },
  {
    value: "escalate_to_colp",
    label: "Escalate to COLP",
    description: "Decision requires COLP sign-off.",
  },
];

const MLCO_DEBOUNCE_MS = 60_000;
const SHORT_RETRY_MS = 5_000;
const MAX_RATIONALE = 4000;
const MAX_CONDITIONS = 4000;

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email
    ? { "X-Bonsai-User-Id": email }
    : {};
}

export default function MLCOReviewForm({ reviewId, userMeta, matterReference, onDecided, onCancel }) {
  const [outcome, setOutcome] = useState("");
  const [rationale, setRationale] = useState("");
  const [conditions, setConditions] = useState("");
  const [busy, setBusy] = useState(false);
  const lastSubmitRef = useRef(0);
  const callerEmail =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";

  const conditionsRequired = outcome === "approve_with_conditions";

  const canShow = !!(userMeta?.is_mlco || userMeta?.is_colp);

  const submit = useCallback(async () => {
    const now = Date.now();
    if (now - lastSubmitRef.current < MLCO_DEBOUNCE_MS) {
      toast.error("Please wait a moment before resubmitting.");
      return;
    }
    if (!outcome) {
      toast.error("Choose an outcome.");
      return;
    }
    if (!rationale.trim()) {
      toast.error("Rationale is required.");
      return;
    }
    if (conditionsRequired && !conditions.trim()) {
      toast.error("Conditions are required for ‘Approve with conditions’.");
      return;
    }
    if (!callerEmail) {
      toast.error("Cannot submit — missing user email.");
      return;
    }

    lastSubmitRef.current = now;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/compliance/matter-reviews/${reviewId}/mlco-decision`,
        {
          outcome,
          rationale,
          conditions: conditionsRequired ? conditions : null,
          decided_by_email: callerEmail,
        },
        { headers: authHeaders() }
      );
      if (outcome === "escalate_to_colp") {
        toast.success("Escalated to COLP");
      } else {
        toast.success("Decision recorded");
      }
      if (onDecided) onDecided(res.data);
    } catch (err) {
      console.error("MLCO decision failed:", err);
      toast.error(
        errorMessage(err, "Could not save decision. Try again.")
      );
      // Allow re-click after a short window on failure (not the full 60s).
      lastSubmitRef.current = Date.now() - (MLCO_DEBOUNCE_MS - SHORT_RETRY_MS);
    } finally {
      setBusy(false);
    }
  }, [outcome, rationale, conditions, conditionsRequired, callerEmail, reviewId, onDecided]);

  if (!canShow) {
    return null;
  }

  return (
    <div className="space-y-3" data-testid="mlco-review-form">
      {matterReference && (
        <AMLProfileSignalsPanel matterReference={matterReference} />
      )}
      <fieldset
        className="space-y-2"
        role="radiogroup"
        aria-label="MLCO decision outcome"
      >
        {OUTCOMES.map((opt) => {
          const descId = `mlco-outcome-${opt.value}-desc`;
          return (
            <label
              key={opt.value}
              className="flex items-start gap-2 text-sm text-slate-200 cursor-pointer"
              data-testid={`mlco-outcome-${opt.value}-label`}
            >
              <input
                type="radio"
                name="mlco-outcome"
                value={opt.value}
                checked={outcome === opt.value}
                onChange={() => setOutcome(opt.value)}
                disabled={busy}
                aria-describedby={descId}
                data-testid={`mlco-outcome-${opt.value}-radio`}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{opt.label}</span>
                <span
                  id={descId}
                  className="block text-xs text-slate-400 mt-0.5"
                >
                  {opt.description}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div>
        <label
          htmlFor="mlco-rationale"
          className="block text-xs font-medium text-slate-300 mb-1"
        >
          Rationale <span className="text-red-400">*</span>
        </label>
        <textarea
          id="mlco-rationale"
          rows={4}
          maxLength={MAX_RATIONALE}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          disabled={busy}
          placeholder="Explain your decision…"
          className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
          data-testid="mlco-rationale-textarea"
        />
        <div className="text-xs text-slate-500 text-right mt-0.5" data-testid="mlco-rationale-counter">
          {rationale.length} / {MAX_RATIONALE}
        </div>
      </div>

      {conditionsRequired && (
        <div data-testid="mlco-conditions-wrapper">
          <label
            htmlFor="mlco-conditions"
            className="block text-xs font-medium text-slate-300 mb-1"
          >
            Conditions <span className="text-red-400">*</span>
          </label>
          <textarea
            id="mlco-conditions"
            rows={3}
            maxLength={MAX_CONDITIONS}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            disabled={busy}
            placeholder="State the conditions FE must follow…"
            className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
            data-testid="mlco-conditions-textarea"
          />
          <div className="text-xs text-slate-500 text-right mt-0.5">
            {conditions.length} / {MAX_CONDITIONS}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-700/40 text-blue-100 border border-blue-700/60 hover:bg-blue-700/60 disabled:opacity-50"
          data-testid="mlco-decision-submit-btn"
        >
          {busy ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" />
              Saving…
            </span>
          ) : (
            "Save decision"
          )}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600 disabled:opacity-50"
          data-testid="mlco-decision-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
